import { useEffect } from "react";

import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDiscoverTest } from "@/pages/tina/sites/hooks/use-discover-test";
import { useUnblockSession } from "@/pages/tina/sites/hooks/use-unblock-session";

interface Props {
  siteName: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UnblockDialog({ siteName, open, onOpenChange }: Props) {
  const unblock = useUnblockSession(siteName ?? "");
  const verify = useDiscoverTest(siteName ?? "");

  useEffect(() => {
    if (open && siteName) {
      unblock.start();
    }
    if (!open) {
      verify.reset();
      void unblock.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, siteName]);

  const session = unblock.session;
  const isLogin = session?.mode === "login";

  async function onVerify() {
    if (isLogin) await unblock.save();
    verify.start();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            Manual unblock{siteName ? ` · ${siteName}` : ""}
          </DialogTitle>
        </DialogHeader>

        {unblock.starting || !session ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Starting session…
          </div>
        ) : (
          <div className="space-y-3 p-2 text-sm">
            <p>
              {isLogin
                ? "Open the live view, log in, then click Save & Verify."
                : "Open the live view, solve the “not a robot” check, then click Verify."}
            </p>
            <a
              className="underline"
              href={session.live_view_url}
              target="_blank"
              rel="noreferrer"
            >
              Open live view ↗
            </a>
            {verify.isLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Verifying (worker
                cold start can take ~2 min)…
              </div>
            ) : verify.data?.status === "failed" ? (
              <div className="text-destructive">
                Still blocked — solve again, then retry.
              </div>
            ) : verify.data?.status === "done" && verify.data.report ? (
              <div className="text-green-600">
                Unblocked — discovered {verify.data.report.discovered_count}{" "}
                URLs.
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button
            onClick={onVerify}
            disabled={!session || unblock.saving || verify.isLoading}
          >
            {isLogin ? "Save & Verify" : "Verify"}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
